import { Check, Loader2 } from 'lucide-react'

/**
 * A running checklist of the phases a long job has been through.
 *
 * Phases are appended as they arrive rather than matched against a fixed list, because the
 * work is not a fixed list — a script generation may go round the review loop once or three
 * times, and a runbook generation may or may not search documentation. Whatever the server
 * reports becomes a step, the previous one ticks, and the newest spins.
 *
 * A single line that overwrites itself loses that history: you cannot tell a job that is
 * working from one that is stuck, and you never learn what it actually did.
 */
export function PhaseSteps({
    phases,
    done = false,
    className = '',
}: {
    phases: string[]
    /** When the job has finished, the last phase ticks rather than spinning forever. */
    done?: boolean
    className?: string
}) {
    if (phases.length === 0) return null
    return (
        <ol className={`space-y-1.5 ${className}`}>
            {phases.map((phase, i) => {
                const isLast = i === phases.length - 1
                const complete = done || !isLast
                return (
                    <li key={`${i}-${phase}`} className="flex items-start gap-2 text-sm">
                        <span className="mt-0.5 flex-shrink-0">
                            {complete
                                ? <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden />
                                : <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />}
                        </span>
                        <span className={complete ? 'text-muted-foreground' : 'text-foreground'}>{phase}</span>
                    </li>
                )
            })}
        </ol>
    )
}

/**
 * Append a phase, ignoring a repeat of the one already showing.
 *
 * Servers re-send the same status when a step is slow, and a checklist that grew a
 * duplicate line every few seconds would be worse than no checklist.
 */
export function appendPhase(phases: string[], next: string): string[] {
    if (!next || !next.trim()) return phases
    // Compared without trailing dots and case: servers word the same step slightly
    // differently between layers ("Writing the script..." then "writing the script......"),
    // and two near-identical rows read as a stutter rather than progress.
    const key = (s: string) => s.trim().replace(/[.\s]+$/, '').toLowerCase()
    if (phases.length > 0 && key(phases[phases.length - 1]) === key(next)) return phases
    return [...phases, next.trim()]
}
